import _merge from 'lodash/merge';
import { Lightning } from '@lightningjs/sdk';
import { RENDERING_CONTEXT } from '@bedrock-rdk/constants';
import { DETACHED_SIGNAL, INACTIVE_SIGNAL, SELECTED_SIGNAL } from '../signals';
import { getProgress } from '../progress/progress.utils';
import {
  areContentItemsEqual,
  areContextsEqual,
  getTemplateEntry,
  makeTextPatch,
  ICON_POSITION,
} from '../utils/templates';
import Incentive from '../incentive/incentive.component';
import { HEIGHT as INCENTIVE_HEIGHT } from '../incentive/incentive.style';
import FocusShader from '../shaders/focus.shader';
import ImageShader from '../shaders/image.shader';
import {
  defaultContext,
  getInitialStyle,
  getDetailsPosition,
  getFocusedStyle,
  colors,
  getIconViewBox,
  getIcon,
  unFocusedStyle,
  IMAGE_OFFSET,
  TITLES_ORDER,
  IMAGE_MARGIN_BOTTOM,
  PROGRESS_HEIGHT,
  ICON_PADDING_VERTICAL,
} from './card.style';
import {
  sizes,
  templates,
  imageOptions,
  ICONS_TYPES,
  DISPLAY_TITLE,
  DISPLAY_DETAILS,
  DISPLAY_EXTRA_TITLE,
} from './card.constants';
import { getLinesCount } from '../incentive/incentive.utils';

/**
 * Card is a 16:9 tile component with textual metadata at the bottom of a thumbnail
 *
 * @version 2.6.3
 * @see https://zeroheight.com/726d5c282/p/16019d-card--v263
 */
export default class Card extends Lightning.Component {
  /* istanbul ignore next */
  static _template() {
    return _merge(
      {
        Border: {
          rect: true,
          shader: {
            type: FocusShader,
          },
        },
        Image: {
          shader: {
            type: ImageShader,
          },
        },
        TopLeftIcon: {
          type: ICONS_TYPES[ICON_POSITION.TOP_LEFT],
        },
        TopRightIcon: {
          type: ICONS_TYPES[ICON_POSITION.TOP_RIGHT],
        },
        BottomRightIcon: {
          type: ICONS_TYPES[ICON_POSITION.BOTTOM_RIGHT],
        },
        Incentive: {
          type: Incentive,
        },
        ExtraTitle: {},
        Title: {},
        Highlight: {},
        Details: {},
      },
      getInitialStyle(),
    );
  }

  _construct() {
    this._index = 0;
    this._style = { ...defaultContext };
    this._metadataPositionHandler = this._updateMetadataPosition.bind(this);
  }

  _inactive() {
    this.signal(INACTIVE_SIGNAL, this);
  }

  // NOTE: the listeners are bound to the attachment lifecycle rather than to the data, since a recycled view
  // applies data many times but is attached only once
  _attach() {
    TITLES_ORDER.forEach(id => {
      const view = this.tag(id);
      if (view) {
        view.on('txLoaded', this._metadataPositionHandler);
      }
    });
  }

  _detach() {
    TITLES_ORDER.forEach(id => {
      const view = this.tag(id);
      if (view) {
        view.removeListener('txLoaded', this._metadataPositionHandler);
      }
    });
    this._item = undefined;
    this.signal(DETACHED_SIGNAL, this);
  }

  get index() {
    return this._index;
  }

  set index(value) {
    this._index = value;
  }

  get item() {
    return this._item;
  }

  set item({
    content,
    config,
    template,
    colorScheme = defaultContext.colorMode,
    renderingContext = RENDERING_CONTEXT.GENERAL,
  }) {
    const progress = getProgress(content.progress);

    if (areContentItemsEqual(this._item, content) && areContextsEqual(this._renderingContext, renderingContext)) {
      this._item = content;
      // A three level patch object to move one number: the shader is already there, so it is written directly
      this.tag('Image').shader.progress = progress;

      return;
    }
    this._renderingContext = renderingContext;
    this._item = content;
    if (colorScheme && colorScheme !== this._style.colorMode) {
      this._style = { screenSize: this._style.screenSize, colorMode: colorScheme };
    }

    const { screenSize, colorMode } = this._style;
    this._config = config || getTemplateEntry(sizes, template, renderingContext, screenSize);

    const { title, extraTitle, pictos, image } = this._item;
    const { wordWrapWidth } = this;
    const wordWrapWidthHalf = Math.round(wordWrapWidth / 2);

    const { PRIMARY: primaryImage } = getTemplateEntry(imageOptions, template, renderingContext, screenSize);

    const textColor = colors.text[colorMode];
    const imageUrl = (image && image.src) || '';
    const offset = IMAGE_OFFSET[screenSize];
    const viewBox = getIconViewBox(screenSize);

    const topLeftIcon = getIcon(pictos, ICON_POSITION.TOP_LEFT);
    const topRightIcon = getIcon(pictos, ICON_POSITION.TOP_RIGHT);
    const bottomRightIcon = getIcon(pictos, ICON_POSITION.BOTTOM_RIGHT);

    const details = DISPLAY_DETAILS && this._item.details ? this._item.details : '';
    const highlight = this._item.highlight || '';
    const minTextOffset = primaryImage.height + IMAGE_MARGIN_BOTTOM;
    const highlightPatch = makeTextPatch(highlight, details ? wordWrapWidthHalf : wordWrapWidth, textColor);
    const detailsPatch = makeTextPatch(details, highlight ? wordWrapWidthHalf : wordWrapWidth, textColor);
    highlightPatch.y = minTextOffset;
    detailsPatch.y = minTextOffset;

    const incentiveLines = getLinesCount(this._item.incentives);
    this._hasIncentives = incentiveLines > 0;
    const incentiveHeight = this._hasIncentives ? INCENTIVE_HEIGHT[screenSize] : 0;
    this._metadataOffset = primaryImage.height + IMAGE_MARGIN_BOTTOM + incentiveHeight;

    this.patch({
      w: this._config.w,
      h: this._config.h,
      Border: {
        w: primaryImage.width + offset,
        h: primaryImage.height + offset,
      },
      Image: {
        w: primaryImage.width,
        h: primaryImage.height,
        src: imageUrl,
        shader: {
          progress,
        },
      },
      TopLeftIcon: {
        visible: Boolean(topLeftIcon),
        iconData: {
          picto: topLeftIcon,
          viewBox,
        },
      },
      TopRightIcon: {
        visible: Boolean(topRightIcon),
        iconData: {
          picto: topRightIcon,
          viewBox,
        },
      },
      BottomRightIcon: {
        visible: Boolean(bottomRightIcon),
        y: primaryImage.height - ICON_PADDING_VERTICAL - PROGRESS_HEIGHT,
        iconData: {
          picto: bottomRightIcon,
          viewBox,
        },
      },
      Incentive: {
        visible: this._hasIncentives,
        y: primaryImage.height + IMAGE_MARGIN_BOTTOM,
        data: {
          w: primaryImage.width,
          linesCount: incentiveLines,
          incentives: this._item.incentives,
          colorMode,
        },
      },
      ExtraTitle: makeTextPatch(DISPLAY_EXTRA_TITLE ? extraTitle : '', wordWrapWidth, textColor),
      Title: makeTextPatch(DISPLAY_TITLE ? title : '', wordWrapWidth, textColor),
      Highlight: highlightPatch,
      Details: detailsPatch,
    });

    // In case if ID was changed (i.e. full refresh is required) but the content is the same
    this._updateMetadataPosition();
  }

  get wordWrapWidth() {
    return this._config ? this._config.w : 0;
  }

  _updateMetadataPosition() {
    if (!Number.isFinite(this._metadataOffset)) {
      return;
    }

    // NOTE: we are using manual calculation instead of the "flex" layout in order to speed up tile creation
    const [firstLineTag, secondLineTag] = TITLES_ORDER;
    const firstLine = this.tag(firstLineTag);
    const secondLine = this.tag(secondLineTag);
    const highlights = this.tag('Highlight');
    const details = this.tag('Details');

    let offset = this._metadataOffset;

    if (firstLine.visible) {
      firstLine.y = offset;
      offset += firstLine.finalH;
    }

    if (secondLine.visible) {
      secondLine.y = offset;
      offset += secondLine.finalH;
    }

    if (details.visible || highlights.visible) {
      details.patch(getDetailsPosition(highlights.visible));
      details.y = offset;
      highlights.y = offset;
    }
  }

  _focus() {
    this.patch(getFocusedStyle(this._style));
  }

  _unfocus() {
    this.patch(unFocusedStyle);
  }

  _handleEnter() {
    this.signal(SELECTED_SIGNAL, this._item);

    return true;
  }
}

Card.Templates = templates;
Card.Sizes = sizes;
Card.ImageOptions = imageOptions;
